import React, { useState } from 'react';
import { 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  DirectionsBoat as BoatIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  Build as BuildIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  AssignmentInd as RegistrationsIcon,
  Security as SecurityIcon,
  Description as PoliciesIcon,
  Payment as PaymentIcon,
  EventAvailable as AvailabilityIcon,
  Email as EmailIcon,
  AssignmentLate as AssignmentLateIcon,
  Anchor as AnchorIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Campaign as MarketingIcon,
  Queue as QueueIcon,
  Error as ErrorIcon,
  DirectionsBoat as DirectionsBoatIcon,
  Visibility as VisibilityIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MainAdminLayout from './MainAdminLayout';

interface MarinaAdminLayoutProps {
  children: React.ReactNode;
}

const MarinaAdminLayout: React.FC<MarinaAdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for expandable menu sections
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [marinaOpen, setMarinaOpen] = useState(false);
  const [bookingFunnelOpen, setBookingFunnelOpen] = useState(false);

  // Function to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path || 
           (path !== '/admin/dashboard' && location.pathname.startsWith(path));
  };

  // ListItemLink component for navigation links
  const ListItemLink = ({ icon, primary, to }: { icon: React.ReactElement, primary: string, to: string }) => {
    const active = isActive(to);
    
    return (
      <ListItem 
        button 
        component={Link} 
        to={to}
        sx={{
          backgroundColor: active ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          borderLeft: active ? '4px solid' : '4px solid transparent',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          pl: active ? 1.75 : 2,
        }}
      >
        <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={primary} 
          primaryTypographyProps={{ 
            fontWeight: active ? 'bold' : 'regular',
            color: active ? 'primary' : 'inherit'
          }}
        />
      </ListItem>
    );
  };

  const marinaNavigation = (
    <List component="nav">
      <ListItemLink
        to="/admin/dashboard"
        primary="Dashboard"
        icon={<DashboardIcon />}
      />
      
      {/* Booking Funnel Section */}
      <ListItem button onClick={() => setBookingFunnelOpen(!bookingFunnelOpen)}>
        <ListItemIcon>
          <ErrorIcon />
        </ListItemIcon>
        <ListItemText primary="Booking Funnel" />
        {bookingFunnelOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={bookingFunnelOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/marina/session-recordings"
            primary="Session Recordings"
            icon={<VisibilityIcon />}
          />
        </List>
      </Collapse>
      
      {/* Equipment Section */}
      <ListItem button onClick={() => {
        navigate('/admin/equipment');
        setEquipmentOpen(!equipmentOpen);
      }}>
        <ListItemIcon>
          <BoatIcon />
        </ListItemIcon>
        <ListItemText primary="Equipment" />
        {equipmentOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
              <Collapse in={equipmentOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemLink
              to="/admin/equipment/vessels/powered"
              primary="Powered Vessels"
              icon={<BoatIcon />}
            />
            <ListItemLink
              to="/admin/equipment/vessels/nonpowered"
              primary="Non-powered Vessels"
              icon={<BoatIcon />}
            />
            <ListItemLink
              to="/admin/equipment/trailers"
              primary="Trailers"
              icon={<BuildIcon />}
            />
            <ListItemLink
              to="/admin/equipment/accessories"
              primary="Accessories & Safety"
              icon={<InventoryIcon />}
            />
          </List>
        </Collapse>
      
      {/* Marina Management Section */}
      <ListItem button onClick={() => setMarinaOpen(!marinaOpen)}>
        <ListItemIcon>
          <AnchorIcon />
        </ListItemIcon>
        <ListItemText primary="Marina Management" />
        {marinaOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={marinaOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/settings/marina"
            primary="Slip Management"
            icon={<AnchorIcon />}
          />
          <ListItemLink
            to="/admin/marina/contracts"
            primary="Contract Management"
            icon={<PoliciesIcon />}
          />
          <ListItemLink
            to="/admin/waiting-list"
            primary="Waiting List"
            icon={<QueueIcon />}
          />
          <ListItemLink
            to="/admin/marina/launch-activity"
            primary="Launch Activity"
            icon={<DirectionsBoatIcon />}
          />
        </List>
      </Collapse>
      
      {/* Pricing Section */}
      <ListItem button onClick={() => setPricingOpen(!pricingOpen)}>
        <ListItemIcon>
          <MoneyIcon />
        </ListItemIcon>
        <ListItemText primary="Pricing" />
        {pricingOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={pricingOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/pricing"
            primary="Pricing Rules"
            icon={<MoneyIcon />}
          />
          <ListItemLink
            to="/admin/pricing/discounts"
            primary="Discounts"
            icon={<MoneyIcon />}
          />
          <ListItemLink
            to="/admin/pricing/waiver"
            primary="Damage Waiver"
            icon={<MoneyIcon />}
          />
        </List>
      </Collapse>
      
      {/* Registrations Section */}
      <ListItemLink
        to="/admin/registrations"
        primary="Registrations"
        icon={<AssignmentLateIcon />}
      />
      
      {/* Settings Section */}
      <ListItem button onClick={() => setSettingsOpen(!settingsOpen)}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="Settings" />
        {settingsOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemLink
            to="/admin/settings"
            primary="Marina Settings"
            icon={<SettingsIcon />}
          />
          <ListItemLink
            to="/admin/settings/payment"
            primary="Payment Settings"
            icon={<MoneyIcon />}
          />
          <ListItemLink
            to="/admin/settings/policies"
            primary="Policies"
            icon={<SettingsIcon />}
          />
          <ListItemLink
            to="/admin/settings/transactional-email-templates"
            primary="Transactional Email Templates"
            icon={<EmailIcon />}
          />
          {/* <ListItemLink
            to="/admin/settings/images"
            primary="Image Management"
            icon={<PhotoLibraryIcon />}
          /> */}
        </List>
      </Collapse>
    </List>
  );

  return (
    <MainAdminLayout drawerContent={marinaNavigation}>
      {children}
    </MainAdminLayout>
  );
};

export default MarinaAdminLayout; 